// Consent widget: data-365="consent"
// Renders a venue's active consent forms (365-mandatory + custom) and records acceptance — reusing the
// audit-grade UserConsent capture on the platform (server stamps IP/UA/version).
//
// Reading is ANONYMOUS (the active-forms endpoint is AllowAnonymous — a visitor can always read what
// they're being asked to sign); only RECORDING consent is authenticated (sign-in redirect if needed).
// A signed-in user who already consented sees an "on file" state per form (matched by venueConsentFormId
// + version from GET /api/consent/me) instead of being silently re-asked; a form whose version changed
// since they signed is flagged for re-review. Required forms are enforced client-side: submit is blocked
// until every required (still-editable) form is accepted. Minor forms collect the guardian attestation +
// minor name/DOB (native date input), which the API turns into a ConsentSubject.
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, footer, clear, renderLoading, renderError, signOutRow } from '../ui';

interface ConsentForm {
  id: string;
  isMandatoryTemplate: boolean;
  title: string;
  bodyMarkdown: string;
  version: string;
  isRequired: boolean;
  appliesToMinors: boolean;
}

/** A row from GET /api/consent/me (the user's consent audit trail; newest first). */
interface MyConsentRow {
  venueConsentFormId?: string | null;
  granted?: boolean;
  version?: string | null;
  acceptedAtUtc?: string | null;
}

interface FormState {
  accepted: boolean;
  minorName: string;
  minorDob: string;
  guardianName: string;
  guardianRelationship: string;
  guardianEmail: string;
}

type OnFile = { granted: boolean; version: string; when: string | null };

const asUtc = (s: string): Date => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
const fmtDate = (s: string | null): string => {
  if (!s) return '';
  try { return asUtc(s).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; }
};

export function mountConsent(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'consent') auth.clearPending();
  void render(root, cfg, api, auth);
}

async function render(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, opts?: { justRecorded?: boolean; reviewMode?: boolean }): Promise<void> {
  clear(root);
  root.append(h('h3', undefined, 'Consent'));
  root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px; font-size:.85rem;' },
    'This facility is photographed, recorded, and live-streamed. Please review the forms below.'));

  const host = h('div');
  root.append(host);
  renderLoading(host, 'Consent');

  // Forms are PUBLIC — always load and show them, signed in or not.
  let forms: ConsentForm[];
  try {
    forms = await api.get<ConsentForm[]>(`/api/venue-consent-forms/active/${cfg.venueId}`);
  } catch {
    clear(host);
    renderError(host, 'We couldn’t load the consent forms. Please try again.', () => void render(root, cfg, api, auth));
    return;
  }

  clear(host);
  if (!forms || forms.length === 0) {
    host.append(h('div', { class: 'e365-alert info' }, 'No consent forms are required here right now.'), footer());
    return;
  }

  // What's already on file for this user (latest row per form; /me returns newest-first).
  const onFile = new Map<string, OnFile>();
  if (auth.isLoggedIn) {
    try {
      const rows = await api.get<MyConsentRow[]>('/api/consent/me');
      for (const r of rows || []) {
        if (!r.venueConsentFormId || onFile.has(r.venueConsentFormId)) continue;
        onFile.set(r.venueConsentFormId, { granted: !!r.granted, version: r.version || '', when: r.acceptedAtUtc || null });
      }
    } catch { /* auth lapse or outage — fall through to the editable view; recording will re-auth */ }
  }

  if (opts?.justRecorded) host.append(h('div', { class: 'e365-alert info' }, '✓ Thank you — your consent is recorded.'));

  const isCurrent = (f: ConsentForm) => {
    const r = onFile.get(f.id);
    return !!r && r.granted && r.version === f.version;
  };

  // "All set" when every form is either consented at its current version, or is a minors-only form the
  // user has never signed (an adult without a kid never signs the minor release — that's not "pending").
  const settled = auth.isLoggedIn && forms.every((f) => isCurrent(f) || (f.appliesToMinors && !onFile.get(f.id)));
  if (settled && !opts?.reviewMode) {
    if (!opts?.justRecorded) host.append(h('div', { class: 'e365-alert info' }, '✓ You’re all set — your consents are on file.'));
    for (const f of forms) {
      host.append(h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin:4px 0;' },
        isCurrent(f)
          ? `✓ ${f.title} — consented ${fmtDate(onFile.get(f.id)!.when)}`
          : `· ${f.title} — only needed if you're signing for a minor`));
    }
    const review = h('button', { class: 'e365-slot', style: 'margin-top:10px;' }, 'Review or update my answers') as HTMLButtonElement;
    review.addEventListener('click', () => void render(root, cfg, api, auth, { reviewMode: true }));
    host.append(review,
      signOutRow(auth.profile?.email, () => { auth.logout(); void render(root, cfg, api, auth); }),
      footer());
    return;
  }

  // Editable view: every form card is readable; already-current forms show their on-file status and are
  // excluded from re-submission unless the user opts to change them (reviewMode makes all editable).
  const state = new Map<string, FormState>();
  const editable = new Set<string>();
  for (const f of forms) {
    state.set(f.id, { accepted: false, minorName: '', minorDob: '', guardianName: '', guardianRelationship: '', guardianEmail: '' });
    if (opts?.reviewMode || !isCurrent(f)) editable.add(f.id);
  }

  for (const f of forms) host.append(renderForm(f, state.get(f.id)!, onFile.get(f.id), editable.has(f.id), auth.isLoggedIn));

  const msg = h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:8px;' });

  if (!auth.isLoggedIn) {
    // Reading needed no account; recording does. Sign-in returns here and the widget re-renders.
    const login = h('button', { class: 'e365-btn', style: 'margin-top:10px;' }, 'Sign in to record your consent') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'consent' }));
    host.append(login,
      h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:8px;' }, 'You’ll sign in to your 365 account so your consent is on record.'),
      footer());
    return;
  }

  const submit = h('button', { class: 'e365-btn', style: 'margin-top:10px;' }, 'I agree') as HTMLButtonElement;
  submit.addEventListener('click', () => void doSubmit(cfg, api, auth, root, forms, state, editable, submit, msg));
  host.append(submit, msg,
    signOutRow(auth.profile?.email, () => { auth.logout(); void render(root, cfg, api, auth); }),
    footer());
}

// A minors release marked "required" is required FOR MINORS, not for every adult — label it that way
// and never hard-block an adult on it (the server records what's submitted either way).
function reqLabel(f: ConsentForm): string {
  return f.isRequired ? (f.appliesToMinors ? '(required for minors)' : '(required)') : '(optional)';
}

function renderForm(f: ConsentForm, st: FormState, prior: OnFile | undefined, editable: boolean, loggedIn: boolean): HTMLElement {
  const card = h('div', {
    style: 'border:1px solid var(--e365-border); border-radius:var(--e365-radius); padding:12px; margin-bottom:10px; background:var(--e365-surface);',
  });

  const details = h('details', { style: 'margin-top:6px;' },
    h('summary', { style: 'cursor:pointer; font-size:.85rem;' }, 'Read the full text'),
    h('div', { style: 'white-space:pre-wrap; font-size:.8rem; line-height:1.4; margin-top:6px;' }, f.bodyMarkdown));

  // Already consented at the current version and not reviewing → status row, no checkbox.
  if (!editable) {
    card.append(
      h('div', { style: 'font-weight:600;' }, `${f.title} ${reqLabel(f)}`),
      h('div', { style: 'font-size:.85rem; color:#0a7d33; margin-top:4px;' }, `✓ Consented ${fmtDate(prior?.when ?? null)} — on file`),
      details);
    return card;
  }

  const cb = h('input', { type: 'checkbox' }) as HTMLInputElement;
  const label = h('label', { style: 'display:flex; gap:8px; align-items:center; font-weight:600; cursor:pointer;' },
    cb, h('span', undefined, `${f.title} ${reqLabel(f)}`));
  card.append(label);

  // Context lines for a signed-in user with history on this form.
  if (loggedIn && prior) {
    if (prior.granted && prior.version !== f.version) {
      card.append(h('div', { class: 'e365-alert warn', style: 'font-size:.8rem; margin-top:6px;' },
        `This form has been updated since you signed it on ${fmtDate(prior.when)} — please review and accept the new version.`));
    } else if (prior.granted) {
      card.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:4px;' },
        `Previously consented ${fmtDate(prior.when)}. Leave unchecked to record a withdrawal, or check to re-confirm.`));
    } else {
      card.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:4px;' },
        `You previously declined this on ${fmtDate(prior.when)}.`));
    }
  }

  const minorBox = h('div', { style: 'display:none; margin-top:8px;' });
  minorBox.append(
    minorInput('Minor’s full name', 'text', (v) => (st.minorName = v)),
    minorInput('Minor’s date of birth', 'date', (v) => (st.minorDob = v)),
    minorInput('Parent/guardian name', 'text', (v) => (st.guardianName = v)),
    minorInput('Relationship to minor', 'text', (v) => (st.guardianRelationship = v)),
    minorInput('Guardian email (optional)', 'text', (v) => (st.guardianEmail = v)),
  );

  cb.addEventListener('change', () => {
    st.accepted = cb.checked;
    if (f.appliesToMinors) minorBox.style.display = cb.checked ? 'block' : 'none';
  });

  card.append(details);
  if (f.appliesToMinors) card.append(minorBox);
  return card;
}

function minorInput(labelText: string, type: 'text' | 'date', onInput: (v: string) => void): HTMLElement {
  const input = h('input', {
    type,
    placeholder: labelText,
    'aria-label': labelText,
    ...(type === 'date' ? { max: new Date().toISOString().slice(0, 10) } : {}),
    style: 'width:100%; padding:8px; margin-top:6px; border:1px solid var(--e365-border); border-radius:6px; box-sizing:border-box; background:var(--e365-surface); color:inherit; font:inherit;',
  }) as HTMLInputElement;
  input.addEventListener('input', () => onInput(input.value));
  if (type !== 'date') return input;
  // Date inputs show no placeholder — pair with a visible mini-label so the field isn't a mystery box.
  return h('div', undefined,
    h('div', { class: 'e365-muted', style: 'font-size:.75rem; margin-top:6px;' }, labelText),
    input);
}

async function doSubmit(
  cfg: EmbedConfig, api: Api, auth: Auth, root: HTMLElement,
  forms: ConsentForm[], state: Map<string, FormState>, editable: Set<string>,
  btn: HTMLButtonElement, msg: HTMLElement,
): Promise<void> {
  const editableForms = forms.filter((f) => editable.has(f.id));

  // REQUIRED (non-minor) forms must be accepted — a required form left unchecked is almost always a
  // mistake; block and name it. Minor forms are only "required" when consenting for a minor, so they
  // never hard-block an adult.
  for (const f of editableForms) {
    const s = state.get(f.id)!;
    if (f.isRequired && !f.appliesToMinors && !s.accepted) {
      msg.textContent = `“${f.title}” is required — please review and check it to continue.`;
      return;
    }
    if (s.accepted && f.appliesToMinors && (!s.minorName.trim() || !s.minorDob.trim() || !s.guardianName.trim() || !s.guardianRelationship.trim())) {
      msg.textContent = `Please complete the minor and guardian details for “${f.title}”.`;
      return;
    }
  }
  // An untouched minors form is "not applicable", not a decline — leave it out of the submission so the
  // audit trail doesn't fill with false declines from adults. Everything else records granted/declined.
  const submitForms = editableForms.filter((f) => state.get(f.id)!.accepted || !f.appliesToMinors);
  if (submitForms.length === 0) { msg.textContent = 'Nothing to update — your consents are already on file.'; return; }

  btn.disabled = true;
  btn.textContent = 'Recording…';
  const body = {
    venueId: cfg.venueId,
    source: 'Embed/Consent',
    items: submitForms.map((f) => {
      const s = state.get(f.id)!;
      return {
        venueConsentFormId: f.id,
        granted: s.accepted,
        minorFullName: s.minorName || null,
        minorDateOfBirth: s.minorDob || null,
        guardianName: s.guardianName || null,
        guardianEmail: s.guardianEmail || null,
        guardianRelationship: s.guardianRelationship || null,
      };
    }),
  };

  try {
    await api.post('/api/consent/venue', body);
    // Re-render from the server's truth so the cards flip to their "on file" state.
    void render(root, cfg, api, auth, { justRecorded: true });
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'I agree';
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'consent' }); return; }
    const raw = e instanceof Error ? e.message : '';
    const m = raw.match(/\{.*"message"\s*:\s*"([^"]+)"/);
    msg.textContent = m ? m[1] : 'We couldn’t record your consent. Please try again.';
  }
}
